import type { TenantRole, UserRole, UserStatus } from "@nexys/user-management-client";
import {
  Crown,
  ShieldCheck,
  ShieldHalf,
  User,
  UserCog,
  type LucideIcon,
} from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Badge, badgeVariants } from "../ui/badge.js";

type Variant = NonNullable<Parameters<typeof Badge>[0]["variant"]>;

const userRoleMeta: Record<string, { label: string; variant: Variant; icon: LucideIcon }> = {
  superadmin: { label: "Superadmin", variant: "destructive", icon: ShieldCheck },
  useradmin: { label: "User admin", variant: "warning", icon: UserCog },
  user: { label: "User", variant: "secondary", icon: User },
};

const tenantRoleMeta: Record<string, { label: string; variant: Variant; icon: LucideIcon }> = {
  owner: { label: "Owner", variant: "default", icon: Crown },
  admin: { label: "Admin", variant: "warning", icon: ShieldHalf },
  member: { label: "Member", variant: "muted", icon: User },
};

const statusMeta: Record<string, { variant: Variant }> = {
  active: { variant: "success" },
  pending: { variant: "warning" },
  inactive: { variant: "muted" },
};

export const UserRoleBadge = ({
  role,
  className,
}: {
  role: UserRole;
  className?: string;
}) => {
  const meta = userRoleMeta[role] ?? {
    label: role,
    variant: "outline" as Variant,
    icon: User,
  };
  const Icon = meta.icon;
  return (
    <Badge variant={meta.variant} className={cn("capitalize", className)}>
      <Icon className="size-3" aria-hidden />
      {meta.label}
    </Badge>
  );
};

export const TenantRoleBadge = ({
  role,
  className,
}: {
  role: TenantRole;
  className?: string;
}) => {
  const meta = tenantRoleMeta[role] ?? {
    label: role,
    variant: "outline" as Variant,
    icon: User,
  };
  const Icon = meta.icon;
  return (
    <Badge variant={meta.variant} className={cn("capitalize", className)}>
      <Icon className="size-3" aria-hidden />
      {meta.label}
    </Badge>
  );
};

export const StatusBadge = ({
  status,
  className,
}: {
  status: UserStatus;
  className?: string;
}) => {
  const variant = statusMeta[status]?.variant ?? "outline";
  return (
    <Badge variant={variant} className={cn("capitalize", className)}>
      {status}
    </Badge>
  );
};

void badgeVariants; // re-export marker for tree-shaking analysers
